import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { orgApi, type ActivityEntry, type OrgDashboard } from '../../api/organizations';

export function OrganizationDashboardPage() {
  const { id = '' } = useParams();
  const [dashboard, setDashboard] = useState<OrgDashboard | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError('');
    void orgApi
      .getDashboard(id)
      .then(setDashboard)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <p className="p-6 text-slate-600">Loading dashboard…</p>;
  if (error || !dashboard) {
    return <p className="p-6 text-red-600">{error || 'Dashboard unavailable'}</p>;
  }

  const org = dashboard.organization;

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to={`/organizations/${id}`} className="text-sm text-slate-600 hover:underline">
        ← {org.name}
      </Link>
      <h1 className="mt-4 text-2xl font-semibold text-slate-900">Organization Dashboard</h1>
      <p className="mt-1 text-sm text-slate-600">
        {org.slug} · <span className="capitalize">{dashboard.status}</span>
      </p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Contacts" value={dashboard.contact_count} href={`/organizations/${id}?tab=contacts`} />
        <StatCard label="Addresses" value={dashboard.address_count} href={`/organizations/${id}?tab=addresses`} />
        <StatCard label="Documents" value={dashboard.document_count} href={`/organizations/${id}`} />
        <StatCard label="Status" value={dashboard.status} />
      </div>

      <section className="mt-8 rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="font-medium text-slate-900">Recent activity</h2>
        {dashboard.recent_activity.length === 0 ? (
          <p className="mt-3 text-sm text-slate-600">No recent activity.</p>
        ) : (
          <ul className="mt-4 space-y-3">
            {dashboard.recent_activity.map((entry: ActivityEntry) => (
              <li key={entry.id} className="border-l-2 border-slate-200 pl-4 text-sm">
                <p className="font-medium text-slate-900">{entry.summary}</p>
                <p className="text-xs text-slate-500">
                  {entry.action} · {new Date(entry.created_at).toLocaleString()}
                </p>
              </li>
            ))}
          </ul>
        )}
        <Link to={`/organizations/${id}?tab=activity`} className="mt-4 inline-block text-sm text-slate-700 hover:underline">
          View full timeline
        </Link>
      </section>
    </div>
  );
}

function StatCard({ label, value, href }: { label: string; value: string | number; href?: string }) {
  const content = (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-sm text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold capitalize text-slate-900">{value}</p>
    </div>
  );
  if (href) {
    return (
      <Link to={href} className="block transition hover:border-slate-300">
        {content}
      </Link>
    );
  }
  return content;
}
